import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

export interface PendingInsight {
  activityInstanceId: number;
  startedAt: number; // timestamp when polling started
  lastPolledAt: number; // timestamp of last poll
}

const STORAGE_KEY = 'socrative_pending_insights';
const MAX_POLLING_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Actions for managing pending insights
 */
export type PendingInsightAction =
  | { type: 'add'; activityInstanceId: number }
  | { type: 'remove'; activityInstanceId: number }
  | { type: 'updateLastPolled'; activityInstanceId: number };

/**
 * Reducer for pending insights state management
 */
function reducer(
  state: PendingInsight[],
  action: PendingInsightAction
): PendingInsight[] {
  switch (action.type) {
    case 'add': {
      // Check if this activity is already being tracked
      const exists = state.some(
        (insight) => insight.activityInstanceId === action.activityInstanceId
      );
      if (exists) return state;

      const now = Date.now();
      return [
        ...state,
        {
          activityInstanceId: action.activityInstanceId,
          startedAt: now,
          lastPolledAt: now,
        },
      ];
    }
    case 'remove':
      return state.filter(
        (insight) => insight.activityInstanceId !== action.activityInstanceId
      );
    case 'updateLastPolled': {
      return state.map((insight) =>
        insight.activityInstanceId === action.activityInstanceId
          ? { ...insight, lastPolledAt: Date.now() }
          : insight
      );
    }
  }
}

/**
 * Base storage atom - stores pending insights in localStorage
 */
const basePendingInsightsAtom = atomWithStorage<PendingInsight[]>(
  STORAGE_KEY,
  []
);

/**
 * Main pending insights atom with automatic cleanup of expired insights
 * and action-based updates
 */
export const pendingInsightsAtom = atom(
  (get) => {
    const insights = get(basePendingInsightsAtom);
    const now = Date.now();

    // Filter out insights that have been polling for more than 5 minutes
    const validInsights = insights.filter(
      (insight) => now - insight.startedAt < MAX_POLLING_DURATION
    );

    // If we filtered any out, update storage
    if (validInsights.length !== insights.length) {
      // Note: This is a side effect in a getter, but it's for cleanup
      // In a real app, you might want to handle this differently
      setTimeout(() => {
        // Use a microtask to avoid updating during read
        const currentInsights = get(basePendingInsightsAtom);
        const stillValid = currentInsights.filter(
          (insight) => Date.now() - insight.startedAt < MAX_POLLING_DURATION
        );
        if (stillValid.length !== currentInsights.length) {
          // This will be handled by the setter below
        }
      }, 0);
    }

    return validInsights;
  },
  (get, set, action: PendingInsightAction) => {
    const current = get(basePendingInsightsAtom);
    set(basePendingInsightsAtom, reducer(current, action));
  }
);

/**
 * Helper functions for common operations
 */

/**
 * Calculate the next polling interval using exponential backoff
 * Starts at 5 seconds, increases to max of 30 seconds
 */
export function getPollingInterval(insight: PendingInsight): number {
  const elapsedTime = Date.now() - insight.startedAt;
  const baseInterval = 5000; // 5 seconds
  const maxInterval = 30000; // 30 seconds

  // Double the interval every 30 seconds, up to max
  const multiplier = Math.floor(elapsedTime / maxInterval);
  const interval = Math.min(
    baseInterval * Math.pow(2, multiplier),
    maxInterval
  );

  return interval;
}

/**
 * Check if an insight has exceeded the maximum polling duration
 */
export function hasExceededMaxDuration(insight: PendingInsight): boolean {
  return Date.now() - insight.startedAt >= MAX_POLLING_DURATION;
}
